import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "./Button";
import { FormLabel, FormSelect } from "./FormFields";
import type { MapLightPreset, MapStyleName } from "../store/mapViewPrefsStore";

const LIGHT_PRESET_OPTIONS: { value: MapLightPreset; label: string }[] = [
  { value: "dawn", label: "Dawn" },
  { value: "day", label: "Day" },
  { value: "dusk", label: "Dusk" },
  { value: "night", label: "Night" },
];

interface MapLayerControlProps {
  mapStyle: MapStyleName;
  onMapStyleChange: (style: MapStyleName) => void;
  styleSwitching: boolean;
  showTransitLabels: boolean;
  onShowTransitLabelsChange: (value: boolean) => void;
  showPointOfInterestLabels: boolean;
  onShowPointOfInterestLabelsChange: (value: boolean) => void;
  showPlaceLabels: boolean;
  onShowPlaceLabelsChange: (value: boolean) => void;
  showRoadLabels: boolean;
  onShowRoadLabelsChange: (value: boolean) => void;
  lightPreset: MapLightPreset;
  onLightPresetChange: (preset: MapLightPreset) => void;
  show3dObjects: boolean;
  onShow3dObjectsChange: (value: boolean) => void;
  terrainEnabled: boolean;
  onTerrainEnabledChange: (value: boolean) => void;
}

function ToggleChip({
  label,
  active,
  onClick,
  disabled,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="unstyled"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        "inline-flex cursor-pointer items-center justify-center rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors",
        active
          ? "border-blue-600 bg-blue-600 text-white"
          : "border-border bg-white text-primary hover:bg-gray-50",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      {label}
    </Button>
  );
}

export function MapLayerControl({
  mapStyle,
  onMapStyleChange,
  styleSwitching,
  showTransitLabels,
  onShowTransitLabelsChange,
  showPointOfInterestLabels,
  onShowPointOfInterestLabelsChange,
  showPlaceLabels,
  onShowPlaceLabelsChange,
  showRoadLabels,
  onShowRoadLabelsChange,
  lightPreset,
  onLightPresetChange,
  show3dObjects,
  onShow3dObjectsChange,
  terrainEnabled,
  onTerrainEnabledChange,
}: MapLayerControlProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={containerRef}
      data-map-layer-control
      className="pointer-events-auto absolute bottom-8 right-8 z-10"
    >
      {isOpen && (
        <div className="absolute bottom-full right-0 mb-2 w-64 rounded-xl border border-border bg-white p-3 shadow-lg">
          <div className="mb-3">
            <FormLabel>Style</FormLabel>
            <div className="flex gap-1.5">
              <ToggleChip
                label="Standard"
                active={mapStyle === "standard"}
                disabled={styleSwitching}
                onClick={() => onMapStyleChange("standard")}
              />
              <ToggleChip
                label="Satellite"
                active={mapStyle === "standard-satellite"}
                disabled={styleSwitching}
                onClick={() => onMapStyleChange("standard-satellite")}
              />
            </div>
            {styleSwitching && (
              <p className="mt-1 text-2xs text-tertiary">Switching style…</p>
            )}
            <div className="mt-2">
              <FormSelect
                value={lightPreset}
                onChange={(val) => onLightPresetChange(val as MapLightPreset)}
                options={LIGHT_PRESET_OPTIONS}
              />
            </div>
          </div>

          <div className="mb-3">
            <FormLabel>Labels</FormLabel>
            <div className="flex flex-wrap gap-1.5">
              <ToggleChip
                label="Transit"
                active={showTransitLabels}
                onClick={() => onShowTransitLabelsChange(!showTransitLabels)}
              />
              <ToggleChip
                label="Points of interest"
                active={showPointOfInterestLabels}
                onClick={() =>
                  onShowPointOfInterestLabelsChange(!showPointOfInterestLabels)
                }
              />
              <ToggleChip
                label="Places"
                active={showPlaceLabels}
                onClick={() => onShowPlaceLabelsChange(!showPlaceLabels)}
              />
              <ToggleChip
                label="Roads"
                active={showRoadLabels}
                onClick={() => onShowRoadLabelsChange(!showRoadLabels)}
              />
            </div>
          </div>

          <div>
            <FormLabel>3D</FormLabel>
            <div className="flex flex-wrap gap-1.5">
              <ToggleChip
                label="Buildings"
                active={show3dObjects}
                onClick={() => onShow3dObjectsChange(!show3dObjects)}
              />
              <ToggleChip
                label="Terrain"
                active={terrainEnabled}
                onClick={() => onTerrainEnabledChange(!terrainEnabled)}
              />
            </div>
          </div>
        </div>
      )}

      <Button
        type="button"
        variant="unstyled"
        icon="layers"
        aria-label="Map layers"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className={clsx(
          "flex size-8 cursor-pointer items-center justify-center rounded-lg border shadow-sm transition-colors",
          isOpen
            ? "border-border bg-subtle text-primary"
            : "border-border bg-white text-tertiary hover:bg-gray-50 hover:text-primary",
        )}
      />
    </div>
  );
}
